// ContactForm.jsx
import { useEffect, useMemo, useState } from "react";
import "./ContactForm.css";
import { trackContactConversion } from "../../../util/gtag";

const STORAGE_KEY = "fit2clean_consent_v1";

function safeParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function hasMarketingConsent() {
  const saved = safeParse(localStorage.getItem(STORAGE_KEY));
  return !!saved?.marketing;
}

function waitForMatelsoReady(timeoutMs = 15000) {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const tick = () => {
      if (window.wmpapi && window.wmpapi.v1 && window.wmpapi.v1.form) return resolve(true);
      if (Date.now() - start > timeoutMs) return reject(new Error("Matelso wmpapi not ready (timeout)."));
      setTimeout(tick, 50);
    };
    tick();
  });
}

function labelForField(dynamicField, language) {
  const localized = dynamicField?.dynamicFieldValues?.find((e) => e.locale === language)?.value;
  return localized || dynamicField?.displayName || "";
}

function dropdownLabel(item, language) {
  const byLocale = item?.labels?.find((d) => d.locale === language)?.value;
  const byEn = item?.labels?.find((d) => d.locale === "en")?.value;
  const any = item?.labels?.[0]?.value;
  return byLocale || byEn || any || "";
}

async function normalizePhoneNumber(phoneInputId, countrySelectId) {
  const phoneInput = document.getElementById(phoneInputId);
  const countrySelect = document.getElementById(countrySelectId);
  const feedbackElement = document.getElementById(`${phoneInputId}_feedback`);

  if (!phoneInput || !countrySelect || !feedbackElement) return;

  if (!phoneInput.value.trim()) {
    feedbackElement.textContent = "";
    phoneInput.dataset.normalized = "";
    return;
  }

  const phoneNumber = phoneInput.value.replace(/[^0-9+]/g, "");
  const countryCode = phoneNumber.startsWith("+") || phoneNumber.startsWith("00") ? "" : countrySelect.value;

  try {
    const result = await window.wmpapi.v1.form.validatePhoneNumber(phoneNumber, countryCode);

    if (result.valid) {
      countrySelect.value = result.regionCode;
      phoneInput.value = result.nationalNumber;
      phoneInput.dataset.normalized = result.normalized;
      feedbackElement.textContent = "";
    } else {
      phoneInput.dataset.normalized = "";
      feedbackElement.textContent = "Ungültige Telefonnummer";
    }
  } catch {
    phoneInput.dataset.normalized = "";
    feedbackElement.textContent = "Fehler bei der Telefonnummern-Prüfung";
  }
}

function ContactForm() {
  const [status, setStatus] = useState("idle");
  const [fields, setFields] = useState([]);
  const [fieldTypes, setFieldTypes] = useState(null);
  const [phoneCountryCodes, setPhoneCountryCodes] = useState([]);
  const [language, setLanguage] = useState("de");
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        await waitForMatelsoReady();

        const formFields = await window.wmpapi.v1.form.getFields();
        const types = window.wmpapi.v1.form.fieldDataType;
        const codes = await window.wmpapi.v1.form.config.phoneCountryCodes;
        const lang = window.wmpapi.v1.config?.language || "de";

        formFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        setFields(formFields);
        setFieldTypes(types);
        setPhoneCountryCodes(Array.isArray(codes) ? codes : []);
        setLanguage(lang);
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 5000);
    return () => clearTimeout(t);
  }, [flash]);

  const phoneDefault = useMemo(() => {
    if (!phoneCountryCodes?.length) return "";
    if (phoneCountryCodes.includes("DE")) return "DE";
    if (phoneCountryCodes.includes("+49")) return "+49";
    return phoneCountryCodes[0];
  }, [phoneCountryCodes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setFlash(null);

    const formEl = e.currentTarget;

    const formData = new FormData(formEl);
    const payload = {};

    formData.forEach((value, key) => {
      // CSS.escape kann in sehr alten Browsern fehlen – fallback ist ok:
      const safeKey = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(key) : key.replace(/"/g, '\\"');
      const element = formEl.querySelector(`[name="${safeKey}"]`);
      if (!element) return;

      payload[key] = element.dataset?.normalized || (element.type === "checkbox" ? element.checked.toString() : value);
    });

    // 1) NUR Matelso: wenn das fehlschlägt => Fehler anzeigen
    try {
      await window.wmpapi.v1.form.send(payload);
    } catch (err) {
      console.error(err);
      setStatus("error");
      setFlash({ type: "error", text: "Leider ist ein Fehler aufgetreten. Bitte später erneut versuchen." });
      return;
    }

    // 2) Erfolg UI — darf den Erfolg NICHT kaputt machen
    setStatus("success");
    setFlash({ type: "success", text: "Vielen Dank! Wir melden uns schnellstmöglich bei Ihnen." });

    try {
      formEl.reset();
    } catch (err) {
      console.error(err);
    }

    // 3) Tracking — darf den Erfolg NICHT kaputt machen
    // FIX: gtag.js laden & config abwarten, dann Conversion senden (await)
    try {
      const consentMarketing = hasMarketingConsent();
      if (consentMarketing) {
        await trackContactConversion();
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <section className="contact-form-container fade-in-up">
      <div className="contact-form-inner hover-lift">
        <h2>Kontakt aufnehmen</h2>
        <p>Wir freuen uns auf Ihre Anfrage – wir melden uns schnell zurück!</p>
        <p><strong>Wir suchen aktuell keine neuen Arbeitskräfte.</strong></p>

        {flash && (
          <div className={`messagebox ${flash.type === "success" ? "messagebox--success" : "messagebox--error"}`}>
            {flash.text}
          </div>
        )}

        <form className="contact-form" onSubmit={handleSubmit}>
          {fields.map((field) => {
            const { id, dynamicField } = field;
            const { displayName, isRequired, validationPattern, dynamicFieldDataType, dropdownItems } = dynamicField || {};

            const label = labelForField(dynamicField, language);
            const requiredMark = isRequired ? " *" : "";

            if (!fieldTypes) return null;

            if (dynamicFieldDataType === fieldTypes.PHONENUMBER) {
              const countrySelectId = `${id}_country`;
              const phoneInputId = `${id}_phone`;

              return (
                <div className="form-group" key={id}>
                  <label htmlFor={phoneInputId}>
                    {label}
                    {requiredMark}
                  </label>

                  <div className="phone-row">
                    <select
                      className="phone-country"
                      id={countrySelectId}
                      defaultValue={phoneDefault}
                      onChange={() => normalizePhoneNumber(phoneInputId, countrySelectId)}
                    >
                      {phoneCountryCodes.map((code) => (
                        <option key={code} value={code}>
                          {code}
                        </option>
                      ))}
                    </select>

                    <input
                      className="phone-input"
                      type="tel"
                      id={phoneInputId}
                      name={displayName}
                      required={!!isRequired}
                      pattern={validationPattern || undefined}
                      placeholder="z. B. 15123456789"
                      data-normalized=""
                      onChange={() => normalizePhoneNumber(phoneInputId, countrySelectId)}
                      onBlur={() => normalizePhoneNumber(phoneInputId, countrySelectId)}
                    />
                  </div>

                  <div className="phone-feedback" id={`${phoneInputId}_feedback`} />
                </div>
              );
            }

            if (dynamicFieldDataType === fieldTypes.MULTILINETEXT) {
              return (
                <div className="form-group" key={id}>
                  <label htmlFor={id}>
                    {label}
                    {requiredMark}
                  </label>
                  <textarea id={id} name={displayName} required={!!isRequired} rows={5} placeholder="Worum geht es?" />
                </div>
              );
            }

            if (dynamicFieldDataType === fieldTypes.BOOLEAN) {
              return (
                <div className="form-group" key={id}>
                  <label htmlFor={id}>
                    {label}
                    {requiredMark}
                  </label>
                  <input id={id} name={displayName} type="checkbox" />
                </div>
              );
            }

            if (dynamicFieldDataType === fieldTypes.NUMBER) {
              return (
                <div className="form-group" key={id}>
                  <label htmlFor={id}>
                    {label}
                    {requiredMark}
                  </label>
                  <input id={id} name={displayName} type="number" required={!!isRequired} />
                </div>
              );
            }

            if (dynamicFieldDataType === fieldTypes.DATE) {
              return (
                <div className="form-group" key={id}>
                  <label htmlFor={id}>
                    {label}
                    {requiredMark}
                  </label>
                  <input id={id} name={displayName} type="date" required={!!isRequired} />
                </div>
              );
            }

            if (dynamicFieldDataType === fieldTypes.DROPDOWN) {
              return (
                <div className="form-group" key={id}>
                  <label htmlFor={id}>
                    {label}
                    {requiredMark}
                  </label>
                  <select id={id} name={displayName} required={!!isRequired}>
                    <option value="">Select...</option>
                    {(dropdownItems || []).map((item) => (
                      <option key={item.cDataId} value={item.cDataId}>
                        {dropdownLabel(item, language)}
                      </option>
                    ))}
                  </select>
                </div>
              );
            }

            // --- TEXTFELDER (E-Mail, Vorname, Nachname, Firma, etc.) ---
            const labelLower = (label || "").toLowerCase();
            const nameLower = (displayName || "").toLowerCase();

            const isEmail = labelLower.includes("mail") || nameLower.includes("mail");
            const isFirstName = labelLower.includes("vorname") || nameLower.includes("vorname");
            const isLastName = labelLower.includes("nachname") || nameLower.includes("nachname");
            const isCompany = labelLower.includes("firma") || nameLower.includes("firma") || labelLower.includes("unternehmen");

            let currentPlaceholder = "";
            if (isEmail) {
              currentPlaceholder = "beispiel@email.de";
            } else if (isFirstName) {
              currentPlaceholder = "z. B. Max";
            } else if (isLastName) {
              currentPlaceholder = "z. B. Mustermann";
            } else if (isCompany) {
              currentPlaceholder = "z. B. Muster GmbH";
            } else if (labelLower.includes("name") || nameLower.includes("name")) {
              currentPlaceholder = "Vor- und Nachname"; // Fallback, falls nur "Name" übergeben wird
            }

            return (
              <div className="form-group" key={id}>
                <label htmlFor={id}>
                  {label}
                  {requiredMark}
                </label>
                <input
                  type={isEmail ? "email" : "text"}
                  id={id}
                  name={displayName}
                  required={!!isRequired}
                  pattern={validationPattern || undefined}
                  placeholder={currentPlaceholder}
                />
              </div>
            );
          })}

          <button type="submit" className="btn" disabled={status === "sending"}>
            {status === "sending" ? "Sende..." : "Nachricht senden"}
          </button>
        </form>
      </div>
    </section>
  );
}

export default ContactForm;